import React from 'react';
import axios from "axios";

import { Container, Grid, useMantineTheme, Box, Group, Flex } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconFilter, IconReload, IconCirclePlus } from '@tabler/icons-react';

import { useDisplayModeContext } from "../../context/displayModeContext";
import { useLoginContext } from "../../context/loginContext";

import { BE_API_ONE, utils } from "../../utils/globalVariables";
import { regexNoStriped } from "../../utils/globalFunctions";

import AmountOfData from "../../components/general/AmountOfData";
import DisplayModeSelector from "../../components/general/DisplayModeSelector";
import ActionIconComponent from "../../components/general/ActionIconComponent";

import NoData from "../../components/general/NoData";
import WrapperUtils from "../../layout/WrapperUtils";

import CustomerCard from "./CustomerCard";
import CustomerTable from "./CustomerTable";
import CustomerAdd from "./CustomerAdd";
import CustomerEdit from "./CustomerEdit";
import CustomerDelete from "./CustomerDelete";
import CustomerView from "./CustomerView";
import CustomerFilter from "./CustomerFilter";
import SearchInputComponent from "../../components/general/SearchInputComponent";

const CustomerIndex = ({ stateMenuList }) => {

    const mountedRef = React.useRef(true);

    const theme = useMantineTheme();
    const matchesMinMD = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
    const { displayMode } = useDisplayModeContext();
    const { loginContext, handleSetLoginContext } = useLoginContext();

    const notificationId = React.useRef({
        errorGetData: 'customerIndex-1',
        errorGetDataCustomerCategory: 'customerIndex-2',
        errorGetDataCustomerGroup: 'customerIndex-3',
    })

    const filterDefaultRef = React.useRef({
        customerIsActive: utils.flagAll,
        customerGroupId: utils.flagAll,
        customerGender: Object.values(utils.gender).map(obj => String(obj.value)),
    })

    const sortList = {
        customerName: {
            slug: 'customer_name',
            caption: 'Nama',
            type: utils.sortType.string.slug
        },
        customerCategory: {
            slug: 'ms_c.customer_category_slug',
            caption: 'Kategori',
            type: utils.sortType.string.slug
        },
        customerGender: {
            slug: 'customer_gender',
            caption: 'Gender',
            type: utils.sortType.integer.slug
        },
        customerAddress: {
            slug: 'customer_address',
            caption: 'Alamat',
            type: utils.sortType.string.slug
        },
        customerDateCreated: {
            slug: 'customer_created_datetime',
            caption: 'Tanggal Input',
            type: utils.sortType.date.slug
        },
        customerIsActive: {
            slug: 'customer_is_active',
            caption: 'Status',
            type: utils.sortType.integer.slug
        },
    }

    const [data, setData] = React.useState({
        data: [],
        dataCount: 0,
        dataFlag: 'idle',
        dataLoading: false,
        dataRefresh: new Date(),
        dataOffset: 0,
        dataSortSelected: sortList.customerName,
        dataSearchInputForBE: '',

        dataCustomerCategory: [],
        dataCustomerCategoryLoading: false,
        dataCustomerCategoryFlag: 'idle',

        dataCustomerGroup: [],
        dataCustomerGroupLoading: false,
        dataCustomerGroupFlag: 'idle',

        filterDrawer: false,
        filterCustomerIsActive: filterDefaultRef.current.customerIsActive,
        filterCustomerGender: filterDefaultRef.current.customerGender,
        filterCustomerCategory: utils.flagAll,
        filterCustomerGroupId: filterDefaultRef.current.customerGroupId,

        addDrawer: false,

        editCustomer: {
            drawer: false,
            data: []
        },

        deleteCustomer: {
            drawer: false,
            data: []
        },

        viewCustomer: {
            drawer: false,
            data: []
        },
    });

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    const fnSort = (stateSortSelected) => {
        setData(d => ({
            ...d,
            dataLoading: true,
            dataSortSelected: {
                ...d.dataSortSelected,
                slug: d.dataSortSelected.slug[0] === '-' ? stateSortSelected.slug : '-' + stateSortSelected.slug,
                caption: stateSortSelected.caption,
                type: stateSortSelected.type
            }
        }));
    };

    const AmountOfDataComponent = () => {
        return (
            <AmountOfData
                stateDataLimit={utils.dataLimit}
                stateDataOffset={data.dataOffset}
                stateAmountDataAll={data.dataCount}
                stateDataFlag={data.dataFlag}
                stateSuffix=''

                fnFirstData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnLastData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnPreviousData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}

                fnNextData={(stateNewOffset) => setData(d => ({
                    ...d,
                    dataLoading: true,
                    dataRefresh: new Date(),
                    dataOffset: stateNewOffset
                }))}
            />
        )
    }

    React.useEffect(() => {
        setData(d => ({ ...d, dataLoading: true }));

        const formDT = new FormData();
        const formDTHeader = { 'Content-Type': 'application/json' }

        formDT.append('json_token', loginContext.syswebapp);
        formDT.append('data_limit', utils.dataLimit);
        formDT.append('data_offset', data.dataOffset);
        formDT.append('order_by', regexNoStriped(data.dataSortSelected.slug));
        formDT.append('order_type', data.dataSortSelected.slug[0] === '-' ? 'desc' : 'asc');

        formDT.append('flag_all', utils.flagAll);
        formDT.append('filter_search', data.dataSearchInputForBE);
        formDT.append('filter_customer_is_active', data.filterCustomerIsActive);
        formDT.append('filter_customer_category_is_active', utils.flagAll);

        formDT.append('filter_customer_gender', JSON.stringify(data.filterCustomerGender));
        formDT.append('filter_customer_category_slug', data.filterCustomerCategory === utils.flagAll ? utils.flagAll : JSON.stringify(data.filterCustomerCategory));
        formDT.append('filter_customer_group_id', data.filterCustomerGroupId);

        axios({
            method: 'post',
            url: BE_API_ONE + '/get_customer',
            data: formDT,
            headers: formDTHeader,
        }).then((response) => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, dataLoading: false }));

            if (response.data.flag_status === 'isset') {
                setData(d => ({
                    ...d,
                    data: response.data.data,
                    dataCount: response.data.data_count,
                    dataFlag: response.data.data.length > 0 ? true : false,
                }));
            }

            if (response.data.flag_status === 'invalid-json-token') {
                handleSetLoginContext(null);
            }

            if (response.data.flag_status === 'user-inactive') {
                handleSetLoginContext(null);
            }
        }).catch(error => {
            if (!mountedRef.current) return null;

            setData(d => ({
                ...d,
                dataLoading: false,
            }));

            notifications.show({
                id: notificationId.current.errorGetData,
                title: utils.notification.warning.caption,
                message: error.message,
                color: utils.notification.warning.color,
                icon: utils.notification.warning.icon,
                withBorder: true,
                autoClose: true,
            })
        })
    }, [handleSetLoginContext, loginContext.syswebapp, data.dataRefresh, data.dataSearchInputForBE, data.dataSortSelected.slug, data.dataOffset, data.filterCustomerIsActive, data.filterCustomerGender, data.filterCustomerCategory, data.filterCustomerGroupId])

    React.useEffect(() => {
        setData(d => ({ ...d, dataCustomerCategoryLoading: true }));

        const formDT = new FormData();
        const formDTHeader = { 'Content-Type': 'application/json' }

        formDT.append('json_token', loginContext.syswebapp);
        formDT.append('flag_all', utils.flagAll);
        formDT.append('filter_customer_category_is_active', utils.flagAll);

        axios({
            method: 'post',
            url: BE_API_ONE + '/get_customer_category',
            data: formDT,
            headers: formDTHeader,
        }).then((response) => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, dataCustomerCategoryLoading: false }));

            if (response.data.flag_status === 'isset') {
                setData(d => ({
                    ...d,
                    dataCustomerCategory: response.data.data,
                    dataCustomerCategoryFlag: response.data.data.length > 0 ? true : false,
                }));
            }

            if (response.data.flag_status === 'invalid-json-token') {
                handleSetLoginContext(null);
            }

            if (response.data.flag_status === 'user-inactive') {
                handleSetLoginContext(null);
            }
        }).catch(error => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, dataCustomerCategoryLoading: false }));

            notifications.show({
                id: notificationId.current.errorGetDataCustomerCategory,
                title: utils.notification.warning.caption,
                message: error.message,
                color: utils.notification.warning.color,
                icon: utils.notification.warning.icon,
                withBorder: true,
                autoClose: true,
            })
        })
    }, [handleSetLoginContext, loginContext.syswebapp])

    React.useEffect(() => {
        setData(d => ({ ...d, dataCustomerGroupLoading: true }));

        const formDT = new FormData();
        const formDTHeader = { 'Content-Type': 'application/json' }

        formDT.append('json_token', loginContext.syswebapp);
        formDT.append('flag_all', utils.flagAll);
        formDT.append('filter_customer_group_is_active', utils.flagAll);

        axios({
            method: 'post',
            url: BE_API_ONE + '/get_customer_group',
            data: formDT,
            headers: formDTHeader,
        }).then((response) => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, dataCustomerGroupLoading: false }));

            if (response.data.flag_status === 'isset') {
                setData(d => ({
                    ...d,
                    dataCustomerGroup: response.data.data,
                    dataCustomerGroupFlag: response.data.data.length > 0 ? true : false,
                }));
            }

            if (response.data.flag_status === 'invalid-json-token') {
                handleSetLoginContext(null);
            }

            if (response.data.flag_status === 'user-inactive') {
                handleSetLoginContext(null);
            }
        }).catch(error => {
            if (!mountedRef.current) return null;

            setData(d => ({ ...d, dataCustomerGroupLoading: false }));

            notifications.show({
                id: notificationId.current.errorGetDataCustomerGroup,
                title: utils.notification.warning.caption,
                message: error.message,
                color: utils.notification.warning.color,
                icon: utils.notification.warning.icon,
                withBorder: true,
                autoClose: true,
            })
        })
    }, [handleSetLoginContext, loginContext.syswebapp])

    return (
        <React.Fragment>
            <WrapperUtils stateDataLoading={data.dataLoading}>
                <Group spacing='xs' noWrap>
                    <ActionIconComponent
                        stateVariant='light'
                        stateRadius='xl'
                        stateTitle='Filter'
                        stateSize='lg'
                        fnOnClick={() => setData(d => ({ ...d, filterDrawer: true }))}

                        StateIcon={IconFilter}
                        stateIconSize='1.125rem'
                        stateIconStrokeWidth={1.25}
                        stateIconStyle={{
                            color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                        }}
                    />

                    <ActionIconComponent
                        stateVariant='light'
                        stateRadius='xl'
                        stateTitle='Add'
                        stateSize='lg'
                        fnOnClick={() => setData(d => ({ ...d, addDrawer: true }))}

                        StateIcon={IconCirclePlus}
                        stateIconSize='1.25rem'
                        stateIconStrokeWidth={1.25}
                        stateIconStyle={{
                            color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                        }}
                    />

                    <DisplayModeSelector />

                    <ActionIconComponent
                        stateVariant='light'
                        stateRadius='xl'
                        stateTitle='Reload Data'
                        stateSize='lg'
                        fnOnClick={() => setData(d => ({ ...d, dataLoading: true, dataRefresh: new Date() }))}

                        StateIcon={IconReload}
                        stateIconSize='1.125rem'
                        stateIconStrokeWidth={1.25}
                        stateIconStyle={{
                            color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                        }}
                    />

                    <SearchInputComponent
                        statePlaceHolder='Search Customer'
                        fnDoSearch={(val) => setData(d => ({
                            ...d,
                            dataOffset: 0,
                            dataSearchInputForBE: val,
                            dataRefresh: new Date(),
                        }))}
                    />
                </Group>

                {matchesMinMD &&
                    <Flex
                        sx={{ flex: 1 }}
                        justify="flex-end"
                        align="center"
                        direction="row"
                        wrap="nowrap"
                    >
                        <AmountOfDataComponent />
                    </Flex>
                }
            </WrapperUtils>

            <Container fluid>
                <Grid my={0}>
                    {data.data.length <= 0 && data.dataFlag !== 'idle' &&
                        <Grid.Col span={12}>
                            <NoData />
                        </Grid.Col>
                    }

                    {data.data.length > 0 && displayMode === utils.displayMode.card.slug &&
                        <Grid.Col
                            span={12}
                            py={0}
                        >
                            <Box mt='md'>
                                <CustomerCard
                                    stateData={data.data}
                                    stateSortList={sortList}
                                    stateSortSelected={data.dataSortSelected}
                                    fnSort={(val) => {
                                        fnSort(val);
                                    }}

                                    fnSelectedDataView={(val) => setData(d => ({
                                        ...d,
                                        viewCustomer: {
                                            ...d.viewCustomer,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}

                                    fnSelectedDataEdit={(val) => setData(d => ({
                                        ...d,
                                        editCustomer: {
                                            ...d.editCustomer,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}

                                    fnSelectedDataDelete={(val) => setData(d => ({
                                        ...d,
                                        deleteCustomer: {
                                            ...d.deleteCustomer,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}
                                />
                            </Box>
                        </Grid.Col>
                    }

                    {data.data.length > 0 && displayMode === utils.displayMode.table.slug &&
                        <Grid.Col
                            span={12}
                            py={0}
                        >
                            <Box mt='md'>
                                <CustomerTable
                                    stateDataOffset={data.dataOffset}
                                    stateData={data.data}
                                    stateSortList={sortList}
                                    stateSortSelected={data.dataSortSelected}
                                    fnSort={(val) => {
                                        fnSort(val);
                                    }}

                                    fnSelectedDataView={(val) => setData(d => ({
                                        ...d,
                                        viewCustomer: {
                                            ...d.viewCustomer,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}

                                    fnSelectedDataEdit={(val) => setData(d => ({
                                        ...d,
                                        editCustomer: {
                                            ...d.editCustomer,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}

                                    fnSelectedDataDelete={(val) => setData(d => ({
                                        ...d,
                                        deleteCustomer: {
                                            ...d.deleteCustomer,
                                            data: val,
                                            drawer: true
                                        }
                                    }))}
                                />
                            </Box>
                        </Grid.Col>
                    }

                    {data.data.length > 0 &&
                        <Grid.Col span={12} className='amountOfDataBottom'>
                            <Group mt='xs' mb='md' position='apart'>
                                <Box>&nbsp;</Box>

                                <AmountOfDataComponent />
                            </Group>
                        </Grid.Col>
                    }
                </Grid>
            </Container>

            <CustomerFilter
                stateShow={data.filterDrawer}
                fnShow={() => setData(d => ({ ...d, filterDrawer: false }))}

                stateDefaultFilter={{
                    ...filterDefaultRef.current,
                    customerCategory: data.dataCustomerCategory,
                    customerGroup: data.dataCustomerGroup,
                }}

                stateLoading={{
                    customerCategory: data.dataCustomerCategoryLoading,
                    customerGroup: data.dataCustomerGroupLoading,
                }}

                stateFlag={{
                    customerCategory: data.dataCustomerCategoryFlag,
                    customerGroup: data.dataCustomerGroupFlag,
                }}

                stateFilter={{
                    filterCustomerIsActive: data.filterCustomerIsActive,
                    filterCustomerCategory: data.filterCustomerCategory,
                    filterCustomerGender: data.filterCustomerGender,
                    filterCustomerGroupId: data.filterCustomerGroupId,
                }}

                fnFilter={(customerIsActive, customerCategory, customerGender, customerGroup) => setData(d => ({
                    ...d,
                    filterCustomerIsActive: customerIsActive,
                    filterCustomerCategory: customerCategory,
                    filterCustomerGender: customerGender,
                    filterCustomerGroupId: customerGroup,
                    dataRefresh: new Date(),
                    dataLoading: true,
                    filterDrawer: false
                }))}
            />

            <CustomerAdd
                stateMenuList={stateMenuList}
                stateShow={data.addDrawer}
                fnShow={() => setData(d => ({ ...d, addDrawer: false }))}

                stateLoading={{
                    customerCategory: data.dataCustomerCategoryLoading,
                    customerGroup: data.dataCustomerGroupLoading,
                }}

                stateFlag={{
                    customerCategory: data.dataCustomerCategoryFlag,
                    customerGroup: data.dataCustomerGroupFlag,
                }}

                stateDefaultData={{
                    customerCategory: data.dataCustomerCategory,
                    customerGroup: data.dataCustomerGroup,
                }}

                fnSuccessAdd={() => {
                    setData(d => ({
                        ...d,
                        addDrawer: false,
                        dataRefresh: new Date(),
                        dataLoading: true,
                    }))
                }}
            />

            <CustomerEdit
                stateShow={data.editCustomer.drawer}
                fnShow={() => setData(d => ({
                    ...d,
                    editCustomer: {
                        ...d.editCustomer,
                        drawer: false
                    }
                }))}

                stateLoading={{
                    customerCategory: data.dataCustomerCategoryLoading,
                    customerGroup: data.dataCustomerGroupLoading,
                }}

                stateFlag={{
                    customerCategory: data.dataCustomerCategoryFlag,
                    customerGroup: data.dataCustomerGroupFlag,
                }}

                stateDefaultData={{
                    customerCategory: data.dataCustomerCategory,
                    customerGroup: data.dataCustomerGroup,
                }}

                stateData={data.editCustomer.data}

                fnSuccessEdit={() => {
                    setData(d => ({
                        ...d,
                        editCustomer: {
                            ...d.editCustomer,
                            drawer: false
                        },
                        dataRefresh: new Date(),
                        dataLoading: true,
                    }))
                }}
            />

            <CustomerDelete
                stateShow={data.deleteCustomer.drawer}
                fnShow={() => setData(d => ({
                    ...d,
                    deleteCustomer: {
                        ...d.deleteCustomer,
                        drawer: false
                    }
                }))}

                stateData={data.deleteCustomer.data}
                fnSuccessDelete={() => {
                    setData(d => ({
                        ...d,
                        deleteCustomer: {
                            ...d.deleteCustomer,
                            drawer: false
                        },
                        dataRefresh: new Date(),
                        dataLoading: true,
                    }))
                }}
            />

            <CustomerView
                stateShow={data.viewCustomer.drawer}
                fnShow={() => setData(d => ({
                    ...d,
                    viewCustomer: {
                        ...d.viewCustomer,
                        drawer: false
                    }
                }))}

                stateData={data.viewCustomer.data}
            />
        </React.Fragment>
    )
}

export default CustomerIndex;